import { all, call, put, fork, takeLatest, select } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { RootState } from 'types';

import { inboxPageActions as actions, SendMessagesFailurePayload } from '.';
import {
  Client,
  GetTemplateLinkType,
  Message,
  SendMessagesType,
  UpdateClientAttributesAndTagsPayload,
} from './types';
import { AddContactsType } from 'app/pages/Users/slice/types';

import api from 'app/api';
import { selectSelectedClientWaNumber } from './selectors';
import { getBusinessIdFromToken } from 'utils/getBusinessId';
import { successModal, errorModal } from 'utils/showSuccessErrorModal';
import { tMark } from 'locales/markForTranslation';
import { getCachedTemplates, setCachedTemplates } from 'utils/indexedDB';
import { CustomError } from 'utils/types/success-error-types';

const toCustomError = (error: unknown): CustomError =>
  (error as AxiosError<CustomError>)?.response?.data ?? ({} as CustomError);

/* Clients APIs */
const INITIAL_CLIENT_PAGE_SIZE = 1000;
const LOAD_MORE_CLIENT_PAGE_SIZE = 500;

export function* getClientListPage(
  action: PayloadAction<{ limit?: number } | undefined>,
) {
  try {
    const state1 = yield select((state: RootState) => state.authPage);
    const limit = action?.payload?.limit ?? INITIAL_CLIENT_PAGE_SIZE;
    const response = yield call(api.get, `/clients?limit=${limit}`);
    yield put(
      actions.fetchClientListPageSuccess({
        data: response.data.data.clients,
        nextCursor: response.data.data.nextCursor ?? null,
        usernameMasking: state1?.permission?.contactNumberMasking,
      }),
    );
  } catch (error) {
    yield put(actions.fetchClientListPageFailure(toCustomError(error)));
  }
}

export function* loadMoreClientList() {
  try {
    const cursor = yield select(
      (state: RootState) => state.inboxPage?.clientListNextCursor,
    );
    if (!cursor) {
      yield put(
        actions.loadMoreClientListSuccess({ data: [], nextCursor: null }),
      );
      return;
    }
    const url = `/clients?limit=${LOAD_MORE_CLIENT_PAGE_SIZE}&cursor=${encodeURIComponent(cursor)}`;
    const response = yield call(api.get, url);
    yield put(
      actions.loadMoreClientListSuccess({
        data: response.data.data.clients,
        nextCursor: response.data.data.nextCursor ?? null,
      }),
    );
  } catch (error) {
    yield put(actions.loadMoreClientListFailure(toCustomError(error)));
  }
}

const FILTER_PAGE_SIZE = 100;
// With the below-window seed a drain rarely needs more than one page; the
// cap only bounds pathological overlap (seed unavailable mid-initial-load).
const FILTER_DRAIN_MAX_PAGES = 4;

// Repeated `tags=a&tags=b` WITHOUT brackets — axios's default `tags[]=` shape
// lands under req.query['tags[]'] on Express 5's 'simple' query parser and
// the filter silently vanishes server-side.
const buildFilterQuery = (
  params: Record<string, unknown>,
  limit: number,
  cursor: string | null,
  seed: { afterTs: number; afterId: string } | null,
): string => {
  const qs = new URLSearchParams();
  qs.set('limit', String(limit));
  if (cursor) qs.set('cursor', cursor);
  else if (seed) {
    qs.set('afterTs', String(seed.afterTs));
    qs.set('afterId', seed.afterId);
  }
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) for (const v of value) qs.append(key, String(v));
    else qs.set(key, String(value));
  }
  return qs.toString();
};

export function* fetchFilteredClients() {
  try {
    const filterFetch = yield select(
      (state: RootState) => state.inboxPage?.filterFetch,
    );
    // Reducer guards set isLoading only when the fetch was accepted.
    if (!filterFetch?.params || !filterFetch.isLoading) return;

    const clientList: Client[] = yield select(
      (state: RootState) => state.inboxPage?.clientList ?? [],
    );
    const known = new Set<string>([
      ...clientList.map(c => c.clientWaNumber),
      ...filterFetch.clients.map((c: Client) => c.clientWaNumber),
    ]);

    // Seed page 1 BELOW the already-loaded window (its oldest chat contact) —
    // the loaded window is covered by the client-side filter, and starting at
    // the top would spend the whole drain loop re-fetching known rows.
    let seed: { afterTs: number; afterId: string } | null = null;
    if (!filterFetch.nextCursor) {
      for (let i = clientList.length - 1; i >= 0; i--) {
        // Promoted rows sit far below the contiguous window — seeding from
        // one would skip every matching chat between the real window bottom
        // and that row.
        if (clientList[i].isFilterPromoted) continue;
        const ts = clientList[i].latestMessage?.timestamp;
        if (ts && clientList[i].id) {
          seed = {
            afterTs: new Date(ts).getTime(),
            afterId: clientList[i].id as string,
          };
          break;
        }
      }
    }

    // Drain loop: pages can still dedupe to zero fresh rows (socket-topped
    // clients, seed unavailable) and a zero-growth fetch never re-fires the
    // virtuoso endReached — keep pulling (bounded) until the list grows or
    // the cursor is exhausted.
    const fresh: Client[] = [];
    let nextCursor: string | null = filterFetch.nextCursor;
    for (let page = 0; page < FILTER_DRAIN_MAX_PAGES; page++) {
      const query = buildFilterQuery(
        filterFetch.params,
        FILTER_PAGE_SIZE,
        nextCursor,
        seed,
      );
      const response = yield call(api.get, `/clients?${query}`);
      const clients: Client[] = response.data.data.clients ?? [];
      nextCursor = response.data.data.nextCursor ?? null;
      for (const c of clients) {
        if (!known.has(c.clientWaNumber)) {
          known.add(c.clientWaNumber);
          fresh.push(c);
        }
      }
      if (!nextCursor || fresh.length >= FILTER_PAGE_SIZE / 2) break;
    }

    yield put(actions.filterFetchSuccess({ data: fresh, nextCursor }));
  } catch (_error) {
    // hasMore flips off in the reducer — a deterministic failure must not be
    // re-issued on every scroll; toggling the filter retries.
    errorModal(tMark('Could not load more filtered chats'));
    yield put(actions.filterFetchFailure());
  }
}

export function* addContacts(
  action: PayloadAction<AddContactsType[] | unknown>,
) {
  try {
    const response = yield call(api.post, `/clients`, action.payload);
    yield put(actions.addContactsSuccess(response.data.data));
    successModal(
      response?.data?.message || tMark('Contact updated successfully'),
    );
  } catch (err) {
    const customError = toCustomError(err);
    yield put(actions.addContactsFailure(customError));
    errorModal(customError?.errorMessage || tMark('Failed to update contact'));
  }
}

export function* updateClientAttributesAndTags(
  action: PayloadAction<UpdateClientAttributesAndTagsPayload[]>,
) {
  try {
    const response = yield call(
      api.put,
      `/clients/attributes-and-tags`,
      action.payload,
    );
    yield put(actions.updateClientAttributesAndTagsSuccess(response.data.data));
    successModal(tMark('Updated successfully'));
  } catch (err) {
    const customError = toCustomError(err);
    yield put(actions.updateClientAttributesAndTagsFailure(customError));
    errorModal(customError?.errorMessage || tMark('Failed to update'));
  }
}

export function* toggleChat(
  action: PayloadAction<{ clientWaNumber: string; isOpen: boolean }>,
) {
  const { clientWaNumber, isOpen } = action.payload;
  try {
    yield call(api.put, `/clients/chat/toggle/${clientWaNumber}`, { isOpen });
    yield put(actions.toggleClientSuccess({ clientWaNumber, result: isOpen }));
    successModal(isOpen ? tMark('Chat opened') : tMark('Chat closed'));
  } catch (error) {
    const customError = toCustomError(error);
    yield put(actions.toggleClientFailure(customError));
    errorModal(customError?.errorMessage || tMark('Failed to toggle chat'));
  }
}

export function* toggleBotForClient(
  action: PayloadAction<{ clientWaNumber: string; isBotReply: boolean }>,
) {
  const { clientWaNumber, isBotReply } = action.payload;
  try {
    yield call(api.put, `/clients/bot/toggle/${clientWaNumber}`, {
      isBotReply,
    });
    yield put(
      actions.toggleBotForClientSuccess({ clientWaNumber, result: isBotReply }),
    );
    successModal(isBotReply ? tMark('Bot enabled') : tMark('Bot disabled'));
  } catch (error) {
    const customError = toCustomError(error);
    yield put(actions.toggleBotForClientFailure(customError));
    errorModal(customError?.errorMessage || tMark('Failed to toggle bot'));
  }
}
export function* toggleBlockClient(
  action: PayloadAction<{ clientWaNumber: string; isBlocked: boolean }>,
) {
  const { clientWaNumber, isBlocked } = action.payload;
  try {
    yield call(api.put, `/clients/chat/block/${clientWaNumber}`, {
      isBlocked,
    });
    yield put(
      actions.toggleBlockClientSuccess({ clientWaNumber, result: isBlocked }),
    );
    successModal(
      isBlocked ? tMark('Client blocked') : tMark('Client unblocked'),
    );
  } catch (error: unknown) {
    const axiosError = error as AxiosError<CustomError>;
    yield put(
      actions.toggleBlockClientFailure(
        axiosError?.response?.data ?? ({} as CustomError),
      ),
    );
    errorModal(
      axiosError?.response?.data?.errorMessage ||
        tMark('Failed to toggle block status'),
    );
  }
}

export function* clearSession(
  action: PayloadAction<{ clientWaNumber: string }>,
) {
  const { clientWaNumber } = action.payload;
  try {
    const response = yield call(
      api.put,
      `/clients/session/clear/${clientWaNumber}`,
    );
    yield put(
      actions.clearSessionSuccess({
        clientWaNumber,
        sessionClearedAt: response.data.data.sessionClearedAt,
        message: response.data.data.message,
      }),
    );
    successModal(tMark('Bot session cleared'));
  } catch (error: unknown) {
    const axiosError = error as AxiosError<CustomError>;
    yield put(
      actions.clearSessionFailure(
        axiosError?.response?.data ?? ({} as CustomError),
      ),
    );
    errorModal(
      axiosError?.response?.data?.errorMessage ||
        tMark('Failed to clear session'),
    );
  }
}

/* Messages APIs */
// Scrolling up pages backwards from the oldest loaded message. Fetching the
// whole history up front made big chats (15k+ messages, ~10MB of JSON) block
// the main thread long enough for Chrome to kill the tab.
// Opening a chat settles inside the load threshold, so one more page is
// prefetched right away — the first scroll-up is already buffered.
const INITIAL_MESSAGE_PAGE_SIZE = 200;
const MESSAGE_PAGE_SIZE = 200;
export function* getMessages(
  action: PayloadAction<{
    clientWaNumber: string | undefined;
    isFromMessages?: boolean;
  }>,
) {
  const { clientWaNumber, isFromMessages } = action.payload;
  try {
    // Opened from a message-search hit or the manual refresh: the target
    // message can sit anywhere in history, so the window has to cover it.
    // Every other open takes one small page and grows on scroll-up.
    const response = yield call(
      api.get,
      isFromMessages
        ? `/messages/${clientWaNumber}`
        : `/messages/${clientWaNumber}?limit=${INITIAL_MESSAGE_PAGE_SIZE}`,
    );
    yield put(actions.fetchMessagesSuccess(response.data.data));
  } catch (error) {
    yield put(actions.fetchMessagesFailure(toCustomError(error)));
  }
}

export function* loadMoreMessages(
  action: PayloadAction<{ clientWaNumber: string }>,
) {
  const { clientWaNumber } = action.payload;
  try {
    // The reducer drops a redundant start, but it cannot stop this saga — so
    // re-check here, otherwise a double startReached issues a wasted request.
    const inbox = yield select((state: RootState) => ({
      messages: state.inboxPage?.selectedClient?.messages,
      openChat: state.inboxPage?.selectedClient?.clientWaNumber,
      hasMore: state.inboxPage?.selectedClient?.hasMoreMessages,
    }));
    const loaded: Message[] = inbox.messages;
    const oldest = loaded?.[0];
    if (
      !inbox.hasMore ||
      inbox.openChat !== clientWaNumber ||
      !oldest?.timestamp ||
      !oldest?.wamid
    ) {
      yield put(actions.loadMoreMessagesFailure({} as CustomError));
      return;
    }

    const response = yield call(
      api.get,
      `/messages/${clientWaNumber}?limit=${MESSAGE_PAGE_SIZE}` +
        `&before=${encodeURIComponent(new Date(oldest.timestamp).toISOString())}` +
        `&beforeId=${encodeURIComponent(oldest.wamid)}`,
    );
    yield put(
      actions.loadMoreMessagesSuccess({
        clientWaNumber,
        messages: response.data.data.messages,
        hasMore: Boolean(response.data.data.hasMore),
      }),
    );
  } catch (error) {
    yield put(actions.loadMoreMessagesFailure(toCustomError(error)));
  }
}

export function* sendMessages(action: PayloadAction<SendMessagesType>) {
  try {
    const response = yield call(api.post, `/messages/send`, {
      messages: action.payload,
    });
    yield put(actions.sendMessagesFailure(response.data.data));
  } catch (err) {
    yield put(
      actions.sendMessagesFailure(
        (err as AxiosError<SendMessagesFailurePayload>)?.response?.data,
      ),
    );
  }
}

export function* getSingleClient(
  action: PayloadAction<{ clientWaNumber: string }>,
) {
  const { clientWaNumber } = action.payload;
  // The selection when this fetch started. If the user opened another chat
  // from the list while the request was in flight, a late response must not
  // take the pane over.
  const selectedBefore: string | undefined = yield select(
    selectSelectedClientWaNumber,
  );
  const overtaken = function* () {
    const selectedNow: string | undefined = yield select(
      selectSelectedClientWaNumber,
    );
    return selectedNow !== selectedBefore && selectedNow !== clientWaNumber;
  };
  try {
    const response = yield call(api.get, `/clients/${clientWaNumber}`);
    if (yield call(overtaken)) return;
    const client = { ...response.data.data, messages: [], isLastMessage: true };
    yield put(actions.setSelectedClient(client));
    yield put(actions.fetchMessagesStart({ clientWaNumber }));
  } catch {
    if (yield call(overtaken)) return;
    yield put(actions.selectClientByUrl({ clientWaNumber }));
  }
}

export function* addTemplateLink(action: PayloadAction<GetTemplateLinkType>) {
  try {
    yield call(api.post, `/templates/media-link`, { ...action.payload });
  } catch (error) {
    console.error('addTemplateLink error:', toCustomError(error));
  }
}

export function* bulkAddTemplateMediaLink(
  action: PayloadAction<GetTemplateLinkType & { targetBusinessId?: number }>,
) {
  try {
    const { targetBusinessId, ...payload } = action.payload;
    // Copy-to-business: ?business_id= re-scopes the request to the target so the
    // media link is stored against that business (checkJwt does the swap).
    const query = targetBusinessId ? `?business_id=${targetBusinessId}` : '';
    yield call(api.post, `/templates/bulk-media-link${query}`, payload);
  } catch (error) {
    console.error('bulkAddTemplateMediaLink error:', toCustomError(error));
  }
}

export function* getTemplateLink() {
  try {
    const response = yield call(api.get, `/templates/media-link`);
    yield put(actions.getTemplateMediaLinkSuccess(response.data.data));
  } catch (error) {
    const customError = toCustomError(error);
    yield put(actions.getTemplateMediaLinkFailure(customError));
    errorModal(
      customError?.errorMessage || tMark('Failed to fetch template links'),
    );
  }
}

export function* getTemplates(
  action: PayloadAction<{ refresh?: boolean } | undefined>,
) {
  const refresh = action.payload?.refresh;
  const businessId = getBusinessIdFromToken();
  try {
    const response = yield call(
      api.get,
      refresh ? '/templates?refresh=true' : '/templates',
    );
    const templates = response.data.data;

    // Persist templates to IndexedDB (non-blocking)
    setCachedTemplates(String(businessId), templates);

    // Migrate: remove old localStorage cache if present
    try {
      localStorage.removeItem(`templates_${businessId}`);
    } catch {
      // ignore
    }

    yield put(actions.getTemplatesSuccess(templates));
  } catch (error) {
    const cached = yield call(getCachedTemplates, String(businessId));
    yield put(actions.getTemplatesSuccess(cached));
    const errorData = (error as AxiosError<CustomError>)?.response?.data;
    if (
      errorData &&
      !JSON.stringify(errorData).includes('ETIMEDOUT') &&
      !JSON.stringify(errorData).includes('ECONNABORTED')
    ) {
      yield put(actions.getTemplatesFailure(errorData));
      errorModal(errorData?.errorMessage || tMark('Failed to fetch templates'));
    }
  }
}

export function* getFlows() {
  const businessId = getBusinessIdFromToken();
  try {
    const response = yield call(api.get, `/flows`);
    localStorage.setItem(
      `flows_${businessId}`,
      JSON.stringify(response.data.data),
    );
    yield put(actions.getFlowsSuccess(response.data.data));
  } catch {
    yield put(
      actions.getFlowsSuccess(
        JSON.parse(localStorage.getItem(`flows_${businessId}`) || '[]'),
      ),
    );
  }
}

function* watchFetchClientListPage() {
  yield takeLatest(actions.fetchClientListPageStart.type, getClientListPage);
}

function* watchLoadMoreClientList() {
  yield takeLatest(actions.loadMoreClientListStart.type, loadMoreClientList);
}

function* watchFetchMessages() {
  yield takeLatest(actions.fetchMessagesStart.type, getMessages);
}

function* watchLoadMoreMessages() {
  yield takeLatest(actions.loadMoreMessagesStart.type, loadMoreMessages);
}

function* watchToggleChat() {
  yield takeLatest(actions.toggleClientStart.type, toggleChat);
}
function* watchToggleBotForClient() {
  yield takeLatest(actions.toggleBotForClientStart.type, toggleBotForClient);
}

function* watchToggleBlockClient() {
  yield takeLatest(actions.toggleBlockClientStart.type, toggleBlockClient);
}

function* watchClearSession() {
  yield takeLatest(actions.clearSessionStart.type, clearSession);
}

function* watchAddContacts() {
  yield takeLatest(actions.addContactsStart.type, addContacts);
}

function* watchFilterFetch() {
  yield takeLatest(
    [actions.filterFetchStart.type, actions.filterFetchMoreStart.type],
    fetchFilteredClients,
  );
}

function* watchUpdateClientAttributesAndTags() {
  yield takeLatest(
    actions.updateClientAttributesAndTagsStart.type,
    updateClientAttributesAndTags,
  );
}

function* watchSendMessages() {
  yield takeLatest(actions.sendMessagesStart.type, sendMessages);
}

function* watchFetchSingleClient() {
  yield takeLatest(actions.fetchSingleClientStart.type, getSingleClient);
}

function* watchGetTemplateMediaLink() {
  yield takeLatest(actions.getTemplateMediaLinkStart.type, getTemplateLink);
}

function* watchBulkAddTemplateMediaLink() {
  yield takeLatest(
    actions.bulkAddTemplateMediaLink.type,
    bulkAddTemplateMediaLink,
  );
}

function* watchAddTemplateMediaLink() {
  yield takeLatest(actions.addTemplateMediaLink.type, addTemplateLink);
}

function* watchGetTemplates() {
  yield takeLatest(actions.getTemplatesStart.type, getTemplates);
}

function* watchGetFlows() {
  yield takeLatest(actions.getFlowsStart.type, getFlows);
}

export function* inboxPageSaga() {
  const sagas = [
    watchFetchClientListPage,
    watchLoadMoreClientList,
    watchFilterFetch,
    watchFetchMessages,
    watchLoadMoreMessages,
    watchToggleChat,
    watchToggleBotForClient,
    watchToggleBlockClient,
    watchClearSession,
    watchAddContacts,
    watchUpdateClientAttributesAndTags,
    watchSendMessages,
    watchFetchSingleClient,
    watchGetTemplateMediaLink,
    watchBulkAddTemplateMediaLink,
    watchAddTemplateMediaLink,
    watchGetTemplates,
    watchGetFlows,
  ];

  yield all(
    sagas.map(saga =>
      fork(function* () {
        while (true) {
          try {
            yield call(saga);
            break;
          } catch (e) {
            console.error('Saga error in inboxPagaSaga:', e);
          }
        }
      }),
    ),
  );
}
